import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { loginUserWithEmailAndPasswordCredentials, fetchEmployeeForMobileAuth } from "./signin.service";
import { setItem, removeItem } from "../../helper/localstorage.helper";
import { updateUser as updateAuthUser, logOut } from "../../../redux/auth/authSlice";

interface ApiResponse {
    statusCode: number;
    message: string;
    response?: {
        userData: [];
    };
}

interface SignInPayload {
    email: string;
    password: string;
}

interface MobileTokenPayload {
    token: string;
    userId?: string | null;
}

interface EmployeeResponse {
    statusCode: number;
    message: string;
    response?: any;
}

export const decodeTokenPayload = (token: string): any => {
    try {
        return JSON.parse(atob(token.split(".")[1]));
    } catch {
        return null;
    }
};

// JWT "exp" is a unix timestamp (seconds); fall back to a 24h session if it can't be read
const getTokenExpiry = (token: string): string => {
    const payload = decodeTokenPayload(token);
    if (payload?.exp) {
        return new Date(payload.exp * 1000).toISOString();
    }
    return new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
};

let user = JSON.parse(localStorage.getItem("user") || 'null');

if (user) {
    if (new Date().getTime() > new Date(user.expireIn).getTime()) {
        user = null;
        localStorage.removeItem("user");
    }
}

const initialState = {
    message: "",
    isError: false,
    isSuccess: false,
    loading: false,
    error: null as string | null,
    user: user?.userData || null,
};

export const signInWithEmailAndPassword = createAsyncThunk(
    "signin/signInWithEmailAndPassword",
    async (user: SignInPayload, thunkAPI) => {
        try {
            const response = await loginUserWithEmailAndPasswordCredentials(user) as ApiResponse;

            if (!(response.statusCode === 200 || response.statusCode === 201)) {
                return thunkAPI.rejectWithValue(response.message);
            }

            if (response.response) {
                setItem("user", JSON.stringify(response.response));
                thunkAPI.dispatch(updateAuthUser(response.response.userData));
                window.dispatchEvent(new Event("userUpdated"));
            }

            return response.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const loginWithMobileToken = createAsyncThunk(
    "signin/loginWithMobileToken",
    async (payload: MobileTokenPayload, thunkAPI) => {
        removeItem("user");

        try {
            setItem("user", JSON.stringify({ token: payload.token }));

            const userId = payload.userId || decodeTokenPayload(payload.token)?.UserId;
            if (!userId) {
                removeItem("user");
                return thunkAPI.rejectWithValue("Invalid mobile login token");
            }

            const response = await fetchEmployeeForMobileAuth(userId) as EmployeeResponse;

            if (!(response.statusCode === 200 || response.statusCode === 201)) {
                removeItem("user");
                return thunkAPI.rejectWithValue(response.message);
            }

            const session = {
                userData: response.response,
                token: payload.token,
                expireIn: getTokenExpiry(payload.token),
            };

            setItem("user", JSON.stringify(session));
            thunkAPI.dispatch(updateAuthUser(session.userData));
            window.dispatchEvent(new Event("userUpdated"));

            return session;
        } catch (error: any) {
            removeItem("user");
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

const signinSlice = createSlice({
    name: "signin",
    initialState,
    reducers: {
        reset: (state) => {
            state.loading = false;
            state.isSuccess = false;
            state.isError = false;
            state.message = "";
            state.error = null;
        },
        clearError: (state) => {
            state.error = null;
            state.isError = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(signInWithEmailAndPassword.pending, (state) => {
                state.loading = true;
                state.isSuccess = false;
                state.isError = false;
                state.message = "";
                state.error = null;
            })
            .addCase(signInWithEmailAndPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.isSuccess = true;
                state.user = action.payload?.userData;
                state.error = null;
            })
            .addCase(signInWithEmailAndPassword.rejected, (state, action) => {
                state.loading = false;
                state.isError = (action?.payload === "account_deactivated" || action?.payload === "inactive_account") ? false : true;
                state.message = action.payload as string;
                state.error = action.payload as string;
                state.user = null;
            })
            .addCase(loginWithMobileToken.pending, (state) => {
                state.loading = true;
                state.isSuccess = false;
                state.isError = false;
                state.message = "";
                state.error = null;
            })
            .addCase(loginWithMobileToken.fulfilled, (state, action) => {
                state.loading = false;
                state.isSuccess = true;
                state.user = action.payload?.userData;
                state.error = null;
            })
            .addCase(loginWithMobileToken.rejected, (state, action) => {
                state.loading = false;
                state.isError = true;
                state.message = action.payload as string;
                state.error = action.payload as string;
                state.user = null;
            })
            // Handle auth logOut action to clear signin state
            .addCase(logOut, () => initialState);
    },
});

export const { reset, clearError } = signinSlice.actions;
export default signinSlice.reducer;
